import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useUpdateClientMutation,
  useUpdateSubscriptionMutation,
} from '@bvi/admin-panel/entities/clients/api-slice';
import { IChangeSubscriptionFormProperties } from '@bvi/admin-panel/feature/clients/ui/client-tab-bar/client-subscription-tab/change-subscription-form/types';
import {
  AccountStatus,
  PaymentMethod,
} from '@bvi/api-interfaces/entity/client';
import { isServerError } from '@bvi/axios-query';
import { Form, FormDatePicker } from '@bvi/common-components';
import { NotistackMessageVariants, useNotistack } from '@bvi/notistack';

import { buildValidationSchema } from './schema';

interface IAccountFormData {
  subscriptionStartsAt: number | Date;
  subscriptionExpiresAt: number | Date;
  subscriptionTermMonths: number;
  accountStatus: AccountStatus;
  paymentMethod?: PaymentMethod | null;
  accountManagerName?: string | null;
  accountManagerEmail?: string | null;
  supportLiaisonName?: string | null;
  supportLiaisonEmail?: string | null;
  pricingNarrative?: string | null;
}

const ACCOUNT_STATUSES = [
  AccountStatus.DoNotActivate,
  AccountStatus.ReadyToActivate,
  AccountStatus.Active,
];

const PAYMENT_METHODS = [
  PaymentMethod.Ach,
  PaymentMethod.Wire,
  PaymentMethod.CreditCard,
  PaymentMethod.Check,
  PaymentMethod.Other,
];

export const ChangeSubscriptionForm: FC<IChangeSubscriptionFormProperties> = ({
  client,
  onSuccess,
}) => {
  const {
    id: clientId,
    subscription,
    accountStatus,
    pricingNarrative,
    paymentMethod,
    accountManagerName,
    accountManagerEmail,
    supportLiaisonName,
    supportLiaisonEmail,
  } = client;
  const [updateSubscription, { isLoading: isUpdatingSubscription, error }] =
    useUpdateSubscriptionMutation();
  const [updateClient, { isLoading: isUpdatingClient, error: clientError }] =
    useUpdateClientMutation();
  const { t } = useTranslation();
  const { showNotificationMessage } = useNotistack();

  const validationSchema = buildValidationSchema(t);
  const methods = useForm<IAccountFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      subscriptionStartsAt: subscription.startsAt,
      subscriptionExpiresAt: subscription.expiresAt,
      subscriptionTermMonths: subscription.termMonths ?? 12,
      accountStatus,
      paymentMethod: paymentMethod ?? undefined,
      accountManagerName: accountManagerName ?? '',
      accountManagerEmail: accountManagerEmail ?? '',
      supportLiaisonName: supportLiaisonName ?? '',
      supportLiaisonEmail: supportLiaisonEmail ?? '',
      pricingNarrative: pricingNarrative ?? '',
    },
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const subscriptionStartsAt = watch('subscriptionStartsAt');
  const subscriptionTermMonths = watch('subscriptionTermMonths');

  useEffect(() => {
    if (subscriptionStartsAt == null || !subscriptionTermMonths) {
      return;
    }

    setValue(
      'subscriptionExpiresAt',
      dayjs(subscriptionStartsAt)
        .add(Number(subscriptionTermMonths), 'month')
        .toDate(),
      { shouldValidate: false },
    );
  }, [subscriptionStartsAt, subscriptionTermMonths, setValue]);

  const onSubmit = async (data: IAccountFormData) => {
    await Promise.all([
      updateSubscription([
        subscription.id,
        {
          subscriptionStartsAt: new Date(data.subscriptionStartsAt).getTime(),
          subscriptionExpiresAt: new Date(data.subscriptionExpiresAt).getTime(),
          subscriptionTermMonths: data.subscriptionTermMonths,
        },
      ]),
      updateClient([
        clientId,
        {
          accountStatus: data.accountStatus,
          paymentMethod: data.paymentMethod ?? null,
          accountManagerName: data.accountManagerName?.trim()
            ? data.accountManagerName
            : null,
          accountManagerEmail: data.accountManagerEmail?.trim()
            ? data.accountManagerEmail
            : null,
          supportLiaisonName: data.supportLiaisonName?.trim()
            ? data.supportLiaisonName
            : null,
          supportLiaisonEmail: data.supportLiaisonEmail?.trim()
            ? data.supportLiaisonEmail
            : null,
          pricingNarrative: data.pricingNarrative ?? null,
        },
      ]),
    ]);
    onSuccess?.();
  };

  useEffect(() => {
    const nextError = error ?? clientError;
    if (isServerError(nextError)) {
      showNotificationMessage(
        { type: nextError.type },
        NotistackMessageVariants.ERROR,
      );
    }
  }, [error, clientError, showNotificationMessage]);

  return (
    <FormProvider {...methods}>
      <Typography variant="h1" mb={2}>
        {t('subscriptions.edit.title')}
      </Typography>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} minWidth="400px">
          <Grid item xs={4}>
            <FormDatePicker
              valueFormat="jsDate"
              field="subscriptionStartsAt"
              label={t('clients.add.form.subscriptionStartsAt.label')}
              error={Boolean(errors.subscriptionStartsAt)}
              helperText={<>{errors.subscriptionStartsAt?.message}</>}
            />
          </Grid>
          <Grid item xs={4}>
            <FormDatePicker
              valueFormat="jsDate"
              field="subscriptionExpiresAt"
              label={t('clients.add.form.subscriptionExpiresAt.label')}
              error={Boolean(errors.subscriptionExpiresAt)}
              helperText={<>{errors.subscriptionExpiresAt?.message}</>}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              {...register('subscriptionTermMonths', { valueAsNumber: true })}
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{ min: 1, step: 1 }}
              label={t('clients.add.form.subscriptionTermMonths.label')}
              error={Boolean(errors.subscriptionTermMonths)}
              helperText={<>{errors.subscriptionTermMonths?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-payment-method"
                error={Boolean(errors.paymentMethod)}
              >
                {t('clients.add.form.paymentMethod.label')}
              </InputLabel>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    label={t('clients.add.form.paymentMethod.label')}
                    labelId="select-payment-method"
                    error={Boolean(errors.paymentMethod)}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {t(`clients.paymentMethods.${method}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-account-status"
                error={Boolean(errors.accountStatus)}
              >
                {t('clients.add.form.accountStatus.label')}
              </InputLabel>
              <Controller
                name="accountStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? AccountStatus.DoNotActivate}
                    label={t('clients.add.form.accountStatus.label')}
                    labelId="select-account-status"
                    error={Boolean(errors.accountStatus)}
                  >
                    {ACCOUNT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {t(`clients.accountStatuses.${status}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.accountStatus && (
                <FormHelperText error>
                  {errors.accountStatus?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('accountManagerName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.accountManagerName.label')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('accountManagerEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.accountManagerEmail.label')}
              error={Boolean(errors.accountManagerEmail)}
              helperText={<>{errors.accountManagerEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('supportLiaisonName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.supportLiaisonName.label')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('supportLiaisonEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.supportLiaisonEmail.label')}
              error={Boolean(errors.supportLiaisonEmail)}
              helperText={<>{errors.supportLiaisonEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register('pricingNarrative')}
              variant="outlined"
              fullWidth
              multiline
              minRows={8}
              label={t('clients.add.form.pricingNarrative.label')}
              placeholder={t('clients.add.form.pricingNarrative.placeholder')}
            />
          </Grid>
          <Grid item xs={12} marginTop={1}>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isUpdatingSubscription || isUpdatingClient}
              autoFocus
            >
              {t('subscriptions.edit.form.submit')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Form>
    </FormProvider>
  );
};
